import {
  QueryArgs,
  TicketTypeItemType,
  TicketTypeType,
  TicketingType,
  TicketingWithUses,
  VisitorTicketType,
  UserTickets,
  UserTicket,
  ScanCount,
  EventDetails,
  UserMyTicket,
  Visitor,
  ExhibitorTicker,
} from "@ac-expo/api-types";
import { privateAxios } from "../axiosInstances";
import { useQuery } from "@tanstack/react-query";
import {
  ticketTypesQueryKeys,
  ticketingQueryKeys,
} from "../query-keys/tickets.query-keys";
import { isAxiosError } from "axios";

export interface Params {
  ids?: string[];
  eventId?: string;
}

type ItemType = "badge" | "ticket";

type TicketOrBadge<T extends ItemType> = T extends "badge"
  ? EventDetails["badgeTypes"]
  : EventDetails["ticketTypes"];

type ItemWithType<T extends ItemType> = TicketOrBadge<T> & { type: T };

export const getEventTicketing = async (eventId: string) => {
  const res = await privateAxios.get<TicketingWithUses>(
    `/events/${eventId}/tickets/ticketing`
  );
  return res.data;
};

export const useGetTicketingQuery = (
  eventId: string,
  args?: QueryArgs<TicketingWithUses>
) => {
  return useQuery({
    queryKey: ticketingQueryKeys.event(eventId),
    queryFn: () => getEventTicketing(eventId),
    ...args,
  });
};

const getEventTicketTypes = async (eventId: string) => {
  const res = await privateAxios.get<TicketTypeItemType[]>(
    `/events/${eventId}/tickets/types`
  );
  return res.data;
};

export const useGetTicketsTypeEventsQuery = <TData = TicketTypeItemType[]>(
  eventId: string,
  args?: QueryArgs<TicketTypeItemType[], TData>
) => {
  return useQuery({
    queryKey: ticketTypesQueryKeys.event(eventId),
    queryFn: () => getEventTicketTypes(eventId),
    ...args,
  });
};

const getTicketType = async (eventId: string, ticketTypeId: string) => {
  const res = await privateAxios.get<TicketTypeType>(
    `/events/${eventId}/tickets/types/${ticketTypeId}`
  );
  return res.data;
};

export const useGetTicketTypeQuery = (
  eventId: string,
  ticketTypeId: string,
  args: QueryArgs<TicketTypeType>
) => {
  return useQuery({
    queryKey: ticketTypesQueryKeys.get(eventId, ticketTypeId),
    queryFn: () => getTicketType(eventId, ticketTypeId),
    ...args,
  });
};

const getExhibitorTickets = async (eventId: string, exhibitorId: string) => {
  const res = await privateAxios.get<UserTicket[]>(
    `/events/${eventId}/tickets/exhibitor/${exhibitorId}`
  );
  return res.data;
};

export const useGetExhibitorTicketsQuery = (
  eventId: string,
  exhibitorId: string
) => {
  return useQuery({
    queryKey: ticketTypesQueryKeys.get(eventId, exhibitorId),
    queryFn: () => getExhibitorTickets(eventId, exhibitorId),
  });
};

const getExhibitorAvailableTickets = async (
  eventId: string,
  exhibitorId: string
) => {
  const res = await privateAxios.get<{
    tickets: ExhibitorTicker[];
    count: number;
  }>(`/events/${eventId}/tickets/exhibitor/${exhibitorId}/available`);
  return res.data;
};

export const useGetExhibitorAvailableTicketsQuery = (
  eventId: string,
  exhibitorId?: string
) => {
  return useQuery({
    enabled: !!exhibitorId && !!eventId,
    queryKey: ticketTypesQueryKeys.get(eventId, exhibitorId!),
    queryFn: () => getExhibitorAvailableTickets(eventId, exhibitorId!),
  });
};

export const getExhibitorGuestsToEventTickets = async (
  structureId: string,
  eventId: string
) => {
  try {
    const { data } = await privateAxios.get<VisitorTicketType[]>(
      `/structures/${structureId}/visitors/tickets/${eventId}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useGetExhibitorGuestsToEventTicketsQuery = (
  structureId: string,
  eventId: string,
  options?: QueryArgs<VisitorTicketType[]>
) => {
  return useQuery({
    queryKey: ticketTypesQueryKeys.ticket(eventId, structureId),
    queryFn: () => getExhibitorGuestsToEventTickets(structureId, eventId),
    ...options,
  });
};

const getExhibitorRemainingTicketsDownloadCount = async (
  structureId: string,
  eventId: string,
) => {
  const { data } = await privateAxios.get<{ count: number }>(
    `/structures/${structureId}/visitors/tickets/${eventId}/remaining-count`,
  );
  return data;
};

export const useGetExhibitorRemainingTicketsDownloadCountQuery = (
  structureId: string,
  eventId: string,
) => {
  return useQuery({
    queryKey: ticketTypesQueryKeys.remainingTicketsCount(eventId, structureId),
    queryFn: () => getExhibitorRemainingTicketsDownloadCount(structureId, eventId),
  });
};

const getExhibitorRemainingBadgesDownloadCount = async (
  structureId: string,
  eventId: string,
) => {
  const { data } = await privateAxios.get<{ count: number }>(
    `/structures/${structureId}/visitors/badges/${eventId}/remaining-count`,
  );
  return data;
};

export const useGetExhibitorRemainingBadgesDownloadCountQuery = (
  structureId: string,
  eventId: string,
) => {
  return useQuery({
    queryKey: ticketTypesQueryKeys.remainingBadgesCount(eventId, structureId),
    queryFn: () => getExhibitorRemainingBadgesDownloadCount(structureId, eventId),
  });
};

const getUserTickets = async () => {
  const res = await privateAxios.get<UserTickets[]>(`/users/tickets`);

  return res.data;
};

export const useGetUserTickets = () => {
  return useQuery({
    queryKey: ticketTypesQueryKeys.all,
    queryFn: getUserTickets,
  });
};

const SCAN_COUNT_REFETCH_INTERVAL_MS = 30 * 1000;

const getScansCount = async (eventId: string) => {
  const res = await privateAxios.get<ScanCount>(
    `/events/${eventId}/tickets/scan/count`
  );

  return res.data;
};

export const useGetScansCount = (eventId: string) => {
  return useQuery({
    queryKey: ticketingQueryKeys.scanCount(eventId),
    queryFn: () => getScansCount(eventId),
    refetchInterval: SCAN_COUNT_REFETCH_INTERVAL_MS,
  });
};

const getAllUserTickets = async () => {
  const res = await privateAxios.get<UserMyTicket[]>(`/users/tickets/all`);
  return res.data;
};

export const useGetAllUserTickets = () => {
  return useQuery({
    queryKey: ticketingQueryKeys.lists(),
    queryFn: () => getAllUserTickets(),
  });
};

const getTicketsData = async <T extends ItemType>(
  params: Params
): Promise<ItemWithType<T>[]> => {
  const res = await privateAxios.get<ItemWithType<T>[]>(
    `/events/tickets/data`,
    {
      params: params,
    }
  );
  return res.data;
};

export const useGetTicketsData = (params: Params) => {
  return useQuery({
    queryKey: ticketingQueryKeys.list(params),
    queryFn: () => getTicketsData(params),
  });
};
